import LibraryElementEnum from 'client/enums/LibraryElementEnum'
import { useAppDispatch } from 'client/hooks/use-dispatch'
import i18n from 'client/i18n'
import { getChildrenEntities } from 'client/reducers/pageReducer'
import { usePage } from 'client/store'
import {
  addStructure,
  removeEntityByIdAndParentId,
} from 'client/store/page/pageActions'
import pageSelectors from 'client/store/page/pageSelectors'
import MenuInterface from 'common/components/entities/Menu/types/MenuInterface'
import { ButtonActionTypeEnum } from 'common/enums/ButtonActionTypeEnum'
import EntityTypeEnum from 'common/enums/entityTypeEnum'
import { RawHtmlInterface } from 'common/types/entities/RawHtmlInterface'
import { TextInterface } from 'common/types/entities/TextInterface'
import { TwitterTweetButtonInterface } from 'common/types/entities/TwitterTweetButtonInterface'
import { useEffect, useRef } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { WebsiteLibraryElementsStructuresCreator } from 'src/editor/pages/website/website-library-elements-structures-creator'
import { createLoginButton } from 'src/shared/entities/login-button/create-login-button'
import { createRawHtml } from 'src/shared/entities/raw-html/create-raw-html'
import { createRegistrationButton } from 'src/shared/entities/registration-button/create-registration-button'
import { createText } from 'src/shared/entities/text/create-text'
import { createTwitterTweetButton } from 'src/shared/entities/twitter-tweet-button/create-twitter-tweet-button'
import { insertChild } from 'src/shared/entities/utils/generate-base-entity'
import { WebsiteButtonEntityInterface } from 'src/shared/entities/website-button/website-button.schema'
import { createWebsiteLanguageSwitcher } from 'src/shared/entities/website-language-switcher/create-website-language-switcher'
import { WebsiteLanguageSwitcherInterface } from 'src/shared/types/entities/website-language-switcher-interface'
import { WebsiteHeaderInterface } from 'src/shared/types/website-entities'
import { getDefaultMarginSettings } from 'src/shared/utils/website-header-utils'

interface UseElementProps {
  entity: WebsiteHeaderInterface
}
export const useElement = ({ entity }: UseElementProps) => {
  const pageLocale = usePage(pageSelectors.getPageLocale)
  const dispatch = useAppDispatch()
  const createWebsiteEntity = new WebsiteLibraryElementsStructuresCreator()
    .create

  const fixedT = i18n.getFixedT(pageLocale)

  const entities = useSelector(
    //@ts-expect-error @TODO ts migration is needed
    state => getChildrenEntities(state.page.present, entity.childIds),
    shallowEqual,
  )
  const awaitingPostToggleRef = useRef(false)
  const prevEntitiesRef = useRef(entities)
  const menu = entities.find(
    (entity: MenuInterface) => entity.type === EntityTypeEnum.Menu,
  )

  const getToggleValue = (element: LibraryElementEnum): boolean => {
    switch (element) {
      case LibraryElementEnum.LibraryWebsiteButton:
        //@ts-expect-error
        return entities.some(el => el.type === EntityTypeEnum.WebsiteButton)
      case LibraryElementEnum.LibraryLoginButton:
        //@ts-expect-error
        return entities.some(el => el.type === EntityTypeEnum.LoginButton)
      case LibraryElementEnum.LibraryRegistrationButton:
        return entities.some(
          //@ts-expect-error
          el => el.type === EntityTypeEnum.RegistrationButton,
        )
      case LibraryElementEnum.LibraryText:
        //@ts-expect-error
        return entities.some(el => el.type === EntityTypeEnum.Text)
      case LibraryElementEnum.LibraryTwitterTweetButton:
        return entities.some(
          //@ts-expect-error
          el => el.type === EntityTypeEnum.TwitterTweetButton,
        )
      case LibraryElementEnum.LibraryRawHTML:
        //@ts-expect-error
        return entities.some(el => el.type === EntityTypeEnum.RawHtml)
      case LibraryElementEnum.LibraryLanguageSwitcher:
        //@ts-expect-error
        return entities.some(el => el.type === EntityTypeEnum.LanguageSwitcher)
      case LibraryElementEnum.LibraryWebsiteLanguageSwitcher:
        return entities.some(
          //@ts-expect-error
          el => el.type === EntityTypeEnum.WebsiteLanguageSwitcher,
        )
      default:
        return false
    }
  }

  const createButtonElement = () => {
    const structure = createWebsiteEntity({
      libraryElementType: LibraryElementEnum.LibraryWebsiteButton,
      container: entity,
      locale: pageLocale,
      position: entity.childIds.length,
    })

    const button = {
      ...Object.values(structure).find(
        entity => entity.type === EntityTypeEnum.WebsiteButton,
      ),
      ...getDefaultMarginSettings(),
      action: ButtonActionTypeEnum.OpenUrl,
    } as WebsiteButtonEntityInterface

    dispatch(addStructure({ ...structure, [button.id]: button }))
  }

  const createLoginButtonElement = () => {
    const button = {
      ...createLoginButton(
        entity.id,
        fixedT('entities.login_button.default_text'),
        '',
      ),
      ...getDefaultMarginSettings(),
    }

    dispatch(
      addStructure({
        [entity.id]: {
          ...entity,
          childIds: insertChild(
            entity.childIds,
            button.id,
            entity.childIds.length,
          ),
        } as WebsiteHeaderInterface,
        [button.id]: button,
      }),
    )
  }

  const createRegistrationButtonElement = () => {
    const button = {
      ...createRegistrationButton(
        entity.id,
        fixedT('entities.registration_button.default_text'),
        '',
      ),
      ...getDefaultMarginSettings(),
    }

    dispatch(
      addStructure({
        [entity.id]: {
          ...entity,
          childIds: insertChild(
            entity.childIds,
            button.id,
            entity.childIds.length,
          ),
        } as WebsiteHeaderInterface,
        [button.id]: button,
      }),
    )
  }

  const createTextElement = () => {
    const text: TextInterface = createText(
      entity.id,
      undefined,
      fixedT('entity_settings.text.header'),
    )

    dispatch(
      addStructure({
        [entity.id]: {
          ...entity,
          childIds: insertChild(
            entity.childIds,
            text.id,
            entity.childIds.length,
          ),
        } as WebsiteHeaderInterface,
        [text.id]: text,
      }),
    )
  }

  const createTwitterTweetButtonElement = () => {
    const button: TwitterTweetButtonInterface = {
      ...createTwitterTweetButton(entity.id),
      ...getDefaultMarginSettings(),
    }

    dispatch(
      addStructure({
        [entity.id]: {
          ...entity,
          childIds: insertChild(
            entity.childIds,
            button.id,
            entity.childIds.length,
          ),
        } as WebsiteHeaderInterface,
        [button.id]: button,
      }),
    )
  }

  const createRawHtmlElement = () => {
    const rawHtml: RawHtmlInterface = {
      ...createRawHtml(entity.id),
      ...getDefaultMarginSettings(),
    }

    dispatch(
      addStructure({
        [entity.id]: {
          ...entity,
          childIds: insertChild(
            entity.childIds,
            rawHtml.id,
            entity.childIds.length,
          ),
        } as WebsiteHeaderInterface,
        [rawHtml.id]: rawHtml,
      }),
    )
  }

  const createWebsiteLanguageSwitcherElement = () => {
    const languageSwitcher: WebsiteLanguageSwitcherInterface = {
      ...createWebsiteLanguageSwitcher(entity.id),
      ...getDefaultMarginSettings(),
    }

    dispatch(
      addStructure({
        [entity.id]: {
          ...entity,
          childIds: insertChild(
            entity.childIds,
            languageSwitcher.id,
            entity.childIds.length,
          ),
        } as WebsiteHeaderInterface,
        [languageSwitcher.id]: languageSwitcher,
      }),
    )
  }

  const removeChildElement = (type: EntityTypeEnum) => {
    const childEntityId = entities.find(
      (entity: { type: EntityTypeEnum }) => entity.type === type,
    )?.id
    if (childEntityId) {
      dispatch(removeEntityByIdAndParentId(childEntityId, entity.id))
    }
  }

  const toggleElement = async (
    value: boolean,
    libraryElement: LibraryElementEnum,
  ) => {
    awaitingPostToggleRef.current = true
    if (value) {
      switch (libraryElement) {
        case LibraryElementEnum.LibraryWebsiteButton:
          return createButtonElement()
        case LibraryElementEnum.LibraryLoginButton:
          return createLoginButtonElement()
        case LibraryElementEnum.LibraryRegistrationButton:
          return createRegistrationButtonElement()
        case LibraryElementEnum.LibraryText:
          return createTextElement()
        case LibraryElementEnum.LibraryTwitterTweetButton:
          return createTwitterTweetButtonElement()
        case LibraryElementEnum.LibraryRawHTML:
          return createRawHtmlElement()
        case LibraryElementEnum.LibraryWebsiteLanguageSwitcher:
          return createWebsiteLanguageSwitcherElement()
        default:
          break
      }
    } else {
      switch (libraryElement) {
        case LibraryElementEnum.LibraryWebsiteButton:
          return removeChildElement(EntityTypeEnum.WebsiteButton)
        case LibraryElementEnum.LibraryLoginButton:
          return removeChildElement(EntityTypeEnum.LoginButton)
        case LibraryElementEnum.LibraryRegistrationButton:
          return removeChildElement(EntityTypeEnum.RegistrationButton)
        case LibraryElementEnum.LibraryText:
          return removeChildElement(EntityTypeEnum.Text)
        case LibraryElementEnum.LibraryTwitterTweetButton:
          return removeChildElement(EntityTypeEnum.TwitterTweetButton)
        case LibraryElementEnum.LibraryRawHTML:
          return removeChildElement(EntityTypeEnum.RawHtml)
        case LibraryElementEnum.LibraryLanguageSwitcher:
          return removeChildElement(EntityTypeEnum.LanguageSwitcher)
        case LibraryElementEnum.LibraryWebsiteLanguageSwitcher:
          return removeChildElement(EntityTypeEnum.WebsiteLanguageSwitcher)
        default:
          break
      }
    }
  }

  useEffect(() => {
    const prev = prevEntitiesRef.current
    const changed = prev !== entities

    if (!awaitingPostToggleRef.current || !changed) {
      prevEntitiesRef.current = entities
      return
    }

    if (entities.length > 2 && menu.alignSelf === 'flex-end') {
      dispatch(
        addStructure({
          [menu.id]: {
            ...menu,
            alignSelf: 'flex-start',
            mobileAlignSelf: 'flex-start',
          } as MenuInterface,
        }),
      )
    }
    if (entities.length <= 2 && menu.alignSelf === 'flex-start') {
      dispatch(
        addStructure({
          [menu.id]: {
            ...menu,
            alignSelf: 'flex-end',
            mobileAlignSelf: 'flex-end',
          } as MenuInterface,
        }),
      )
    }

    awaitingPostToggleRef.current = false
    prevEntitiesRef.current = entities
  }, [entities, menu.alignSelf])

  return { toggleElement, getToggleValue }
}
